import {
	ClientEntityType,
	JobDocumentEntityType,
	JobEntityType,
	JobItemEntityType,
	OrganizationEntityType,
	PatientEntityType,
} from "@/lib/supabase/supabaseTypes";
import { PdfTemplateType } from "../../pdf-templates/document-template.types";
import { KostenvoranschlagTemplate } from "../../pdf-templates/pdf-templates/kostenvoranschlag.template";
import { PdfTemplateConfiguration } from "../../context/PdfTemplateConfigurationContext";
import {
	CalculateTotalsResult,
	calculateSammelrechnungRowsAndTotals,
} from "../../lib/utils/calculate";
import { LieferscheinTemplate } from "../../pdf-templates/pdf-templates/lieferschein.template";
import { GutschriftTemplate } from "../../pdf-templates/pdf-templates/gutschrift.template";
import { RechnungTemplate } from "../../pdf-templates/pdf-templates/rechnung.template";
import { PaymentSlipDataInput } from "../../pdf-templates/pdf-template-components/qr-payment-slip/qr-payment-helpers";
import { SammelrechnungTemplate } from "../../pdf-templates/pdf-templates/sammelrechnung.template";
import { KartenzahlungsquittungTemplate } from "../../pdf-templates/pdf-templates/kartenzahlungsquittung.template";
import { BarzahlungsquittungTemplate } from "../../pdf-templates/pdf-templates/barzahlungsquittung.template";
import { AkontorechnungTemplate } from "../../pdf-templates/pdf-templates/akontorechnung.template";
import { ZahlungserinnerungTemplate } from "../../pdf-templates/pdf-templates/zahlungserinnerung.template";
import { ErsteMahnungTemplate } from "../../pdf-templates/pdf-templates/erste-mahnung.template";
import { FolgemahnungTemplate } from "../../pdf-templates/pdf-templates/folgemahnung.template";
import { AuftragsblattTemplate } from "../../pdf-templates/pdf-templates/auftragsblatt.template";
import { CommonPDFTemplateProps } from "../../pdf-templates/pdf-templates/types";

export const PdfTemplateOptions = ({
	client,
	jobExample,
	jobItems,
	jobDocumentExample,
	organization,
	pdfTemplateConfiguration,
	patientExample,
	jobTotals,
	paymentSlipData,
	sammelrechnungCalculationResult,
	selectedPdfTemplate,
	clientAvatarUrl,
}: {
	client: ClientEntityType;
	jobExample: JobEntityType;
	jobItems: JobItemEntityType[];
	jobDocumentExample: JobDocumentEntityType;
	organization: OrganizationEntityType;
	pdfTemplateConfiguration: PdfTemplateConfiguration | undefined;
	patientExample: PatientEntityType;
	jobTotals: CalculateTotalsResult;
	paymentSlipData: PaymentSlipDataInput;
	sammelrechnungCalculationResult: ReturnType<
		typeof calculateSammelrechnungRowsAndTotals
	>;
	selectedPdfTemplate: PdfTemplateType;
	clientAvatarUrl?: string;
}) => {
	const commonProps: CommonPDFTemplateProps = {
		organization,
		pdfTemplateConfiguration,
		client,
		job: jobExample,
		jobItems: jobItems.slice(0, 10),
		jobDocument: jobDocumentExample,
		jobTotals,
	};
	const templates = {
		[PdfTemplateType.KOSTENVORANSCHLAG]: (
			<KostenvoranschlagTemplate
				{...commonProps}
				patient={patientExample}
			/>
		),
		[PdfTemplateType.LIEFERSCHEIN]: (
			<LieferscheinTemplate {...commonProps} patient={patientExample} />
		),
		[PdfTemplateType.GUTSCHRIFT]: <GutschriftTemplate {...commonProps} />,
		[PdfTemplateType.RECHNUNG]: (
			<RechnungTemplate
				{...commonProps}
				patient={patientExample}
				paymentSlipData={paymentSlipData}
			/>
		),
		[PdfTemplateType.SAMMELRECHNUNG]: (
			<SammelrechnungTemplate
				recipient={client}
				organization={organization}
				pdfTemplateConfiguration={pdfTemplateConfiguration}
				sammelrechnungCalculationResult={
					sammelrechnungCalculationResult
				}
				paymentSlipData={paymentSlipData}
				currentDate={new Date().toISOString()}
			/>
		),

		[PdfTemplateType.KARTENZAHLUNGSQUITTUNG]: (
			<KartenzahlungsquittungTemplate
				{...commonProps}
				paymentSlipData={paymentSlipData}
			/>
		),
		[PdfTemplateType.BARZAHLUNGSQUITTUNG]: (
			<BarzahlungsquittungTemplate
				{...commonProps}
				paymentSlipData={paymentSlipData}
			/>
		),
		[PdfTemplateType.AKONTORECHNUNG]: (
			<AkontorechnungTemplate
				{...commonProps}
				jobItems={jobItems}
				paymentSlipData={paymentSlipData}
				date="01.01.2022" //TODO(pdf)
			/>
		),

		[PdfTemplateType.ZAHLUNGSERRINERUNG]: (
			<ZahlungserinnerungTemplate
				{...commonProps}
				jobItems={jobItems}
				dateInvoice="01.01.2022" // TODO(pdf): Replace with dynamic date
				dateZahlungserinnerung="01.02.2022" // TODO(pdf): Replace with dynamic date
				paymentSlipData={paymentSlipData}
			/>
		),
		[PdfTemplateType.ERSTE_MAHNUNG]: (
			<ErsteMahnungTemplate
				{...commonProps}
				jobItems={jobItems}
				dateMahnung="01.01.2022" // TODO(pdf): Replace with dynamic date
				paymentSlipData={paymentSlipData}
			/>
		),
		[PdfTemplateType.FOLGEMAHNUNG]: (
			<FolgemahnungTemplate
				{...commonProps}
				jobItems={jobItems}
				dateMahnung="01.01.2022" // TODO(pdf): Replace with dynamic date
				paymentSlipData={paymentSlipData}
			/>
		),
		[PdfTemplateType.AUFTRAGSBLATT]: (
			<AuftragsblattTemplate
				{...commonProps}
				jobItems={jobItems}
				patient={patientExample}
				qrCodeUrl="https://www.google.com"
				clientAvatarUrl={clientAvatarUrl}
			/>
		),
		[PdfTemplateType.RECHNUNGSDOPPEL]: <></>,
	};

	return templates[selectedPdfTemplate];
};
